import { useState, useEffect } from "react";
import {
  capitalizeFirstLetter,
  defaultDateFormat,
  formatDateTime,
} from "../../Helper/functionHelper";
import useAuthUser from "../../hooks/useAuthUser";
import useModal from "../../hooks/useModal";
import TableLayout from "../layout/TableLayout";
import {
  NoRecordsFound,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table";
import Button from "../ui/Button";
import { toast } from "react-toastify";
import {
  approveRejectCompoff,
  approveRejectTimesheet,
  deleteTimesheet,
  downloadTimesheetDocument,
  getTimesheetDetails,
} from "../../services/timesheet";
import useConfirm from "../../hooks/useConfirm";
import CheckIcon from "@mui/icons-material/Check";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import { TextField, Tooltip } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Typography, List, ListItem, Divider } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MapView from "../MapView";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandableText from "../common/ExpandableText";
import UserInfoCell from "../common/UserInfoCell";

const formatTimesheetTime = (time) =>
  time?.split(":").slice(0, 2).join(":") || "--";

const COMPOFF_STATUS_MAP = {
  0: { label: "Pending", className: "text-warning" },
  1: { label: "Approved", className: "text-success" },
  2: { label: "Rejected", className: "text-danger" },
};

const TIMESHEET_STATUS_MAP = {
  0: { getStatus: () => "Pending", className: "text-warning" },
  1: { getStatus: (name) => `Approved by ${name}`, className: "text-success" },
  2: { getStatus: (name) => `Rejected by ${name}`, className: "text-danger" },
};

const getTimesheetDayLabel = (code) =>
  code === "WD" ? "Week Day" : code === "WO" ? "Week Off" : "Holiday";

const TimesheetList = ({
  timesheetData = [],
  onFilter,
  activeFilters,
  docPath = "",
}) => {
  const { isAdminHrRole, isManagerRole, isEmployeeRole } = useAuthUser();
  const { showModal, hideModal } = useModal();
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const { confirm } = useConfirm();
  const navigate = useNavigate();

  const tableHeaders = [
    "Day",
    "Project Name",
    "Date",
    "Hours",
    "Shift",
    "Description",
    "Documents",
    "Added On",
    "Comp off Status",
    "Reason",
    "Status",
    "Action",
  ];

  const openModal = (key, title, body) => {
    if (key === "location") {
      const latitude = title;
      const longitude = body;
      body = <MapView latitude={latitude} longitude={longitude} />;
      title = "Location";
    }
    showModal(title, body);
  };

  if (isAdminHrRole || isManagerRole) {
    tableHeaders.unshift("Employee Name (Code)");
    tableHeaders.splice(10, 0, "Compoff Action");
  }
  useEffect(() => {
    setSelectedTimesheets([]);
  }, [timesheetData]);

  const handleCheckboxChange = (timesheetId) => {
    setSelectedTimesheets((prevSelected) =>
      prevSelected.includes(timesheetId)
        ? prevSelected.filter((id) => id !== timesheetId)
        : [...prevSelected, timesheetId],
    );
  };

  const handleSelectAllChange = () => {
    if (
      selectedTimesheets.length ===
      timesheetData.filter((item) => item.timesheetStatus === 0).length
    ) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(
        timesheetData
          .filter((item) => item.timesheetStatus === 0)
          .map((item) => item.timesheetId),
      );
    }
  };

  const isAllSelected =
    selectedTimesheets.length ===
      timesheetData.filter((item) => item.timesheetStatus === 0).length &&
    timesheetData.filter((item) => item.timesheetStatus === 0).length > 0;

  const handleApproveRejectTimesheet = (action) => {
    if (selectedTimesheets.length === 0) {
      toast.error(`Please select at least one record to ${action}`);
      return;
    }
    const title = `${capitalizeFirstLetter(action)} Timesheet`;

    const ApproveRejectForm = () => {
      const [reason, setReason] = useState("");
      const [error, setError] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        if (action === "reject" && reason.trim() === "") {
          setError("Reason is required for rejection.");
          return;
        }

        setLoading(true);

        try {
          const approveRejectData = {
            timesheetIds: selectedTimesheets,
            action,
            approveRejectReason: reason,
          };

          const res = await approveRejectTimesheet(approveRejectData);
          if (res && res.status === 200) {
            const message =
              res.data?.message || "Timesheet updated successfully.";
            toast.success(message);
            onFilter(activeFilters);
          } else {
            const errorMessage =
              res?.data?.message || "Something went wrong. Please try again.";
            toast.error(errorMessage);
          }
        } catch (error) {
          if (error.response) {
            const errorMessage =
              error.response.data?.message || "Server error.";
            toast.error(errorMessage);
          } else {
            toast.error("Network error. Please check your connection.");
          }
        } finally {
          setLoading(false);
          hideModal();
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="dark"
              className="me-2"
              onClick={() => hideModal()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                handleSubmit();
              }}
              disabled={loading}
            >
              {capitalizeFirstLetter(action)}
            </Button>
          </div>
        </div>
      );
    };

    showModal(title, <ApproveRejectForm />);
  };

  const handleApproveRejectCompoff = async (timesheetId, action) => {
    const isConfirmed = await confirm({
      title: `${capitalizeFirstLetter(action)} Compoff`,
      content: `Are you sure you want to ${action} compoff?`,
      confirmText: `${capitalizeFirstLetter(action)}`,
      cancelText: "Cancel",
    });

    if (isConfirmed) {
      try {
        const compoffData = {
          timesheetId,
          compoffAction: action,
        };
        const res = await approveRejectCompoff(compoffData);

        if (res && res.status === 200) {
          const message = res.data?.message || "Compoff updated successfully.";
          toast.success(message);
          onFilter(activeFilters);
        } else {
          const errorMessage =
            res?.data?.message || "Something went wrong. Please try again.";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          toast.error(
            error.response.data.message ||
              "Bad Request. Please check your input.",
          );
        } else {
          console.error("Error while approving or rejecting compoff", error);
          toast.error(error.response?.data?.message || "Something went wrong");
        }
      } finally {
        setSelectedTimesheets([]);
      }
    }
  };

  const handleViewTimesheet = async (timesheetId, savedData) => {
    const response = await getTimesheetDetails(timesheetId);
    const { data } = response.data;

    const formattedDate = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(data.timesheetDate));

    const title = `Timesheet : ${formattedDate}`;

    const hours =
      data?.startTime && data?.endTime
        ? `${formatTimesheetTime(data?.timesheetHours)} (${formatTimesheetTime(
            data?.startTime,
          )} - ${formatTimesheetTime(data?.endTime)})`
        : formatTimesheetTime(data?.timesheetHours);

    const body = (
      <Box
        sx={{
          // background: "var(--color-background-primary)",
          borderRadius: 2,
          overflow: "hidden",
          minWidth: 420,
          maxWidth: 520,
        }}
      >
        {/* Timesheet header */}
        <Box
          sx={{
            background: "var(--color-background-secondary)",
            borderBottom: "0.5px solid",
            borderColor: "divider",
            p: "12px 20px",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* <Box sx={{
            width: 40, height: 40, borderRadius: "50%",
            background: "var(--color-background-info)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <AccessTimeIcon sx={{ color: "var(--color-text-info)", fontSize: 20 }} />
          </Box> */}
          <Box>
            {/* <Typography sx={{ fontWeight: 500, fontSize: 15 }}>
              Timesheet — {data.timesheetDate}
            </Typography> */}
            <Typography
              sx={{ fontSize: 15, fontWeight: 500, color: "text.secondary" }}
            >
              Project:{" "}
              {savedData.projectTitle ? savedData.projectTitle : "Others"}{" "}
              {/* &nbsp;·&nbsp;{" "}
              {data.timesheetDay === "WD"
                ? "Week Day"
                : data.timesheetDay === "WO"
                  ? "Week Off"
                  : "Holiday"} */}
            </Typography>
          </Box>
        </Box>

        {/* Employee details */}
        {(isAdminHrRole || isManagerRole) && (
          <Box
            sx={{
              background: "var(--color-background-info)",
              borderBottom: "0.5px solid",
              borderColor: "divider",
              p: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#E3F2FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--color-text-info)",
                }}
              >
                {(savedData.userName || "")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: 15 }}>
                {savedData.userName || "—"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {savedData.userEmployeeId || "—"}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ p: "16px 20px" }}>
          {/* Metric grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              mb: 2,
            }}
          >
            {[
              {
                label: "Shift type",
                value: `${savedData.shiftTitle ? savedData.shiftTitle : "--"}`,
              },
              { label: "Hours", value: hours ? hours : "--" },
              {
                label: "Day",
                value:
                  data.timesheetDay === "WD"
                    ? "Week Day"
                    : data.timesheetDay === "WO"
                      ? "Week Off"
                      : "Holiday",
              },
              {
                label: "Comp off Status",
                value: Number(savedData?.compoff) === 1
                    ? COMPOFF_STATUS_MAP[savedData?.compoffStatus]?.label
                    : "--",
              },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  background: "var(--color-background-secondary)",
                  borderRadius: 1.5,
                  p: "10px 12px",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", mb: 0.25 }}
                >
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              background: "var(--color-background-secondary)",
              borderRadius: 1.5,
              p: "10px 12px",
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
              Task Name
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.6 }}>
              {data?.task?.taskTitle || "--"}
            </Typography>
          </Box>

          {/* Reason */}
          <Box
            sx={{
              background: "var(--color-background-secondary)",
              borderRadius: 1.5,
              p: "10px 12px",
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
              Reason
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.6 }}>
              {savedData.approveRejectReason || "--"}
            </Typography>
          </Box>

          {/* Description */}
          <Box
            sx={{
              background: "var(--color-background-secondary)",
              borderRadius: 1.5,
              p: "10px 12px",
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
              Description
            </Typography>
            <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {data.description || "--"}
          </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Custom fields */}
          {data.custom_fields?.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 1.25,
                }}
              >
                Custom fields
              </Typography>

              <List dense disablePadding>
                {data.custom_fields.map((field, idx) => (
                  <ListItem
                    key={field.id}
                    disablePadding
                    sx={{
                      py: 1,
                      borderBottom:
                        idx < data.custom_fields.length - 1
                          ? "0.5px solid"
                          : "none",
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{ fontSize: 13, color: "text.secondary" }}
                      >
                        {field.field_key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {field.value || "—"}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      </Box>
    );

    showModal(title, body);
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    const isConfirmed = await confirm({
      title: `Delete Timesheet`,
      content: `Are you sure you want to delete timesheet?`,
      confirmText: `Delete`,
      cancelText: "Cancel",
    });

    if (isConfirmed) {
      try {
        const res = await deleteTimesheet(timesheetId);
        if (res && res.status === 200) {
          const message =
            res.data?.message || "Timesheet deleted successfully.";
          toast.success(message);
          onFilter(activeFilters);
        } else {
          const errorMessage =
            res?.data?.message || "Something went wrong. Please try again.";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          toast.error(
            error.response.data.message ||
              "Bad Request. Please check your input.",
          );
        } else {
          console.error("Error while deleting timesheet", error);
          toast.error(error.response?.data?.message || "Something went wrong");
        }
      }
    }
  };

  const handleEditTimesheet = (timesheetId) => {
    navigate(`/timesheet/edit-timesheet/${timesheetId}`);
  };

  const handleDownloadDocument = async (fileName, documentName) => {
    try {
      const response = await downloadTimesheetDocument(fileName);
      if (response?.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = documentName || "document";
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Failed to download document. Please try again.");
      }
    } catch (error) {
      if (error.response) {
        toast.error(
          error.response.data?.message || "Failed to download document",
        );
      } else if (error.request) {
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("An error occurred while downloading the document");
      }
      console.error("Download Document Error:", error);
    }
  };
  return (
    <>
      <TableLayout>
        <Table
          className={isAdminHrRole || isManagerRole ? "table-col-freeze" : ""}
          id={isAdminHrRole || isManagerRole ? "timesheetmanager_table" : ""}
        >
          <TableHeader>
            <TableRow>
              {tableHeaders.map((head, index) => (
                <TableHead
                  key={index}
                  className={
                    index === tableHeaders.length - 1 ? "last-col-freeze" : ""
                  }
                >
                  {head === "Employee Name (Code)" &&
                  (isAdminHrRole || isManagerRole) ? (
                    <span className="checkbox">
                      <input
                        style={{ marginRight: "6px" }}
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAllChange}
                      />
                      {head}
                    </span>
                  ) : (
                    head
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {timesheetData.length > 0 ? (
              timesheetData.map((item, index) => {
                const {
                  timesheetId,
                  timesheetDay,
                  projectTitle,
                  timesheetDate,
                  startTime,
                  endTime,
                  timesheetHours,
                  shiftTitle,
                  description,
                  document,
                  submittedDate,
                  compoff,
                  compoffStatus,
                  approveRejectReason,
                  timesheetStatus,
                  actionerName,
                  userName,
                  userEmployeeId,
                  userToken,
                  latitude,
                  longitude,
                  rejectCount,
                  actionedOn,
                } = item;

                const showMapIcon =
                  latitude != null &&
                  longitude != null &&
                  latitude !== 0 &&
                  longitude !== 0;

                const isOffDay = timesheetDay === "WO" || timesheetDay === "HD";
                const dayClass = isOffDay ? "text-warning" : "text-success";
                const formattedDay = getTimesheetDayLabel(timesheetDay);
                const formattedDate = defaultDateFormat(timesheetDate) || "--";
                const hours =
                  startTime && endTime
                    ? `${formatTimesheetTime(
                        timesheetHours,
                      )} (${formatTimesheetTime(
                        startTime,
                      )} - ${formatTimesheetTime(endTime)})`
                    : formatTimesheetTime(timesheetHours);

                const compoffData =
                  compoff === "1"
                    ? COMPOFF_STATUS_MAP[compoffStatus] || {
                        label: "--",
                        className: "",
                      }
                    : { label: "--", className: "" };

                const statusData = TIMESHEET_STATUS_MAP[timesheetStatus] || {
                  getStatus: () => "--",
                  className: "",
                };
                const statusLabel = statusData.getStatus(actionerName);
                let showCompoffAction = false;
                if (compoff === "1" && compoffStatus === 0) {
                  showCompoffAction = true;
                }

                let canDelete = false;
                let canEdit = false;
                if (isAdminHrRole && timesheetStatus === 1) {
                  canDelete = true;
                } else if (
                  isEmployeeRole &&
                  timesheetStatus !== 1 &&
                  timesheetStatus !== 2 &&
                  (() => {
                    const submitted = new Date(submittedDate);
                    const deleteDate = new Date(submitted);
                    deleteDate.setDate(deleteDate.getDate() + 1);
                    const now = new Date();
                    return now < deleteDate;
                  })()
                ) {
                  canDelete = true;
                }

                // Pending: editable up to 31 days from submission.
                if (isEmployeeRole && timesheetStatus === 0) {
                  const submitted = new Date(submittedDate);
                  const now = new Date();
                  const diffDays = Math.abs(now - submitted) / (1000 * 60 * 60 * 24);
                  if (diffDays <= 31) {
                    canEdit = true;
                  }
                } else if (isEmployeeRole && timesheetStatus === 2) {
                  // Rejected: resubmit once, only within 6 days of the rejection.
                  const canResubmit = Number(rejectCount) < 2;
                  if (canResubmit && actionedOn) {
                    const rejectedOn = new Date(actionedOn);
                    const now = new Date();
                    const diffDays =
                      (now - rejectedOn) / (1000 * 60 * 60 * 24);
                    if (diffDays <= 7) {
                      canEdit = true;
                    }
                  }
                }

                const documentArray = document ? document.split(",") : [];

                return (
                  <TableRow key={index}>
                    {(isAdminHrRole || isManagerRole) && (
                      <TableCell>
                        <span className="checkbox">
                          {timesheetStatus === 0 && (
                            <input
                              type="checkbox"
                              checked={selectedTimesheets.includes(timesheetId)}
                              onChange={() => handleCheckboxChange(timesheetId)}
                            />
                          )}
                        </span>
                        <UserInfoCell
                          employeeId={userEmployeeId}
                          name={userName}
                          token={userToken}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <span className={dayClass}>{formattedDay}</span>
                    </TableCell>
                    <TableCell>{projectTitle || "Others"}</TableCell>
                    <TableCell>{formattedDate}</TableCell>
                    <TableCell>{hours}</TableCell>
                    <TableCell>{shiftTitle || "--"}</TableCell>
                    <TableCell>
                      <ExpandableText text={description} label="Description" />
                    </TableCell>

                    <TableCell>
                      {documentArray.length > 0
                        ? documentArray.map((doc, docIndex) => {
                            if (doc.startsWith("uploads")) {
                              return (
                                <Tooltip
                                  key={docIndex + 1}
                                  title="Download Document"
                                  placement="top"
                                  arrow
                                >
                                  <DownloadIcon
                                    key={docIndex}
                                    className="cursor-pointer"
                                    onClick={() => {
                                      handleDownloadDocument(
                                        doc,
                                        "Document " + (docIndex + 1),
                                      );
                                    }}
                                  />
                                </Tooltip>
                              );
                            } else {
                              return (
                                <Tooltip
                                  key={docIndex + 1}
                                  title="Download Document"
                                  placement="top"
                                  arrow
                                >
                                  <a
                                    download={`Document ${docIndex + 1}`}
                                    href={docPath + "/" + doc}
                                    key={docIndex}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-dark"
                                  >
                                    <DownloadIcon />
                                  </a>
                                </Tooltip>
                              );
                            }
                          })
                        : "--"}
                    </TableCell>
                    <TableCell>
                      {formatDateTime(submittedDate) || "--"}
                    </TableCell>
                    <TableCell>
                      <span className={compoffData.className}>
                        {compoffData.label}
                      </span>
                    </TableCell>
                    {(isAdminHrRole || isManagerRole) && (
                      <TableCell>
                        <span>
                          {showCompoffAction ? (
                            <>
                              <Tooltip
                                title="Approve Compoff"
                                placement="top"
                                arrow
                              >
                                <CheckIcon
                                  className="cursor-pointer"
                                  onClick={() => {
                                    handleApproveRejectCompoff(
                                      timesheetId,
                                      "approve",
                                    );
                                  }}
                                />
                              </Tooltip>
                              <Tooltip
                                title="Reject Compoff"
                                placement="top"
                                arrow
                              >
                                <DoNotDisturbAltIcon
                                  className="cursor-pointer"
                                  color="error"
                                  onClick={() =>
                                    handleApproveRejectCompoff(
                                      timesheetId,
                                      "reject",
                                    )
                                  }
                                />
                              </Tooltip>
                            </>
                          ) : (
                            "N/A"
                          )}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>
                      <ExpandableText
                        text={approveRejectReason}
                        label="Reason"
                      />
                    </TableCell>
                    <TableCell>
                      <span className={statusData.className}>
                        {statusLabel}
                      </span>
                    </TableCell>
                    <TableCell className="last-col-freeze">
                      <Tooltip title="View" placement="top" arrow>
                        <VisibilityIcon
                          className="cursor-pointer"
                          onClick={() => {
                            handleViewTimesheet(timesheetId, {
                              shiftTitle,
                              approveRejectReason,
                              userName,
                              userEmployeeId,
                              projectTitle,
                              compoff,
                              compoffStatus
                            });
                          }}
                        />
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Delete" placement="top" arrow>
                          <DeleteOutlineOutlinedIcon
                            className="cursor-pointer"
                            color="error"
                            onClick={() => {
                              handleDeleteTimesheet(timesheetId);
                            }}
                          />
                        </Tooltip>
                      )}
                      {showMapIcon && (
                        <Tooltip title="View Location" placement="top" arrow>
                          <LocationOnOutlinedIcon
                            className="cursor-pointer"
                            onClick={() => {
                              openModal("location", latitude, longitude);
                            }}
                          />
                        </Tooltip>
                      )}

                      {canEdit && (
                        <Tooltip title="Edit" placement="top" arrow>
                          <EditOutlinedIcon
                            className="cursor-pointer"
                            onClick={() => {
                              handleEditTimesheet(timesheetId);
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoRecordsFound colSpan={tableHeaders.length} />
            )}
          </TableBody>
        </Table>
      </TableLayout>
      {(isManagerRole || isAdminHrRole) && (
        <div className="action-button-container" id="approverejectbutton">
          <Button
            variant="success"
            size="small"
            onClick={() => handleApproveRejectTimesheet("approve")}
          >
            Approve
          </Button>
          <Button
            variant="success"
            size="small"
            style={{ marginLeft: "5px" }}
            onClick={() => handleApproveRejectTimesheet("reject")}
          >
            Reject
          </Button>
        </div>
      )}
    </>
  );
};

export default TimesheetList;
